import { getAccountsQueryConfig } from '@api/lendingAPI/queryConfigs';
import { TAccounts } from '@helpers/Accounts/accounts.types';
import { getIsMicroLoanFundedAccount } from '@helpers/Accounts/getIsMicroLoanFundedAccount';
import { TFormalOffer } from '@helpers/FormalOffers/formalOffers.types';
import { getDurationInMonths } from '@helpers/GetDurationInMonths/getDurationInMonths';
import { fetchGetFormalOffers } from '@iwoca/lapi-client/edge';
import { useQuery } from '@tanstack/react-query';

import { useStateKey } from '../../../../iwQueryClient/useStateKey';

type TGetMicroLoanOfferViewData = {
    formalOffer: TFormalOffer;
    loanDuration: number;
    isGrowthMicroLoan: boolean;
    isRegressedMicroLoan: boolean;
};

export const useGetMicroLoanOfferViewData = (
    offerId: TFormalOffer['offer_id']
): TGetMicroLoanOfferViewData => {
    const stateKey = useStateKey();

    const { data: formalOffer } = useQuery({
        queryKey: ['formal_offers', stateKey],
        queryFn: () => fetchGetFormalOffers({ stateKey }),
        select: (data) =>
            data.data.formal_offers.find(
                (formalOffer) => formalOffer.offer_id === offerId
            ),
        suspense: true
    });

    const { data: accountsResponse } = useQuery({
        ...getAccountsQueryConfig(stateKey),
        suspense: true
    });

    if (!formalOffer || !accountsResponse) {
        throw new Error();
    }

    const loanDuration = getDurationInMonths(formalOffer.duration!);
    const isRepeatMicroLoan = getIsRepeatMicroLoan(
        accountsResponse.data.accounts
    );

    //Some ML customers will not be able to upgrade to a ML+ (6 month micro loan). Some ML+ customers may no longer qualify for a ML+ and will regress to 3m.
    const isGrowthMicroLoan = Boolean(loanDuration >= 6 && isRepeatMicroLoan);
    const isRegressedMicroLoan = Boolean(loanDuration < 6 && isRepeatMicroLoan);

    return {
        formalOffer,
        loanDuration,
        isGrowthMicroLoan,
        isRegressedMicroLoan
    };
};

const getIsRepeatMicroLoan = (accounts: TAccounts) => {
    return Boolean(
        accounts.find((account) => getIsMicroLoanFundedAccount(account))
    );
};
